import { API_ENDPOINT } from '../constants';
import { HTTP } from './http.service';

// ---------------------------------------------------------------------------
// Tipos alineados con raptor-cpd/src/modules/reports/reports.service.ts y dto/reports.schemas.ts
// ---------------------------------------------------------------------------
export interface ReportSummary {
  from: string;
  to: string;
  total: number;
  answered: number;
  human: number;
  voicemail: number;
  unknown: number;
  bypassed: number;
  billsecSum: number;
  humanRate: number;
  voicemailRate: number;
}

export interface OperatorDailyRow {
  day: string;
  operatorId: string;
  total: number;
  answered: number;
  hangups: number;
  human: number;
  voicemail: number;
  unknown: number;
  billsecSum: number;
}

export interface RouteDailyRow extends Omit<OperatorDailyRow, 'operatorId'> {
  routeGroupId: number;
}

export interface OperatorHourlyRow {
  hour: string;
  operatorId: string;
  total: number;
  answered: number;
  hangups: number;
  human: number;
  voicemail: number;
  unknown: number;
  bypassed: number;
  billsecSum: number;
}

export interface RouteHourlyRow extends Omit<OperatorHourlyRow, 'operatorId'> {
  routeGroupId: number;
}

export interface HangupCauseRow {
  hangupCause: string;
  count: number;
}

export interface DashboardReport {
  summary: ReportSummary;
  topOperators: OperatorDailyRow[];
  topRoutes: RouteDailyRow[];
  topHangupCauses: HangupCauseRow[];
}

export type CpdVerdictFilter = 'human' | 'voicemail' | 'unknown' | 'bypass';

export interface CallItem {
  callUuid: string;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  direction: string;
  operatorId: string;
  routeGroupId: number;
  aNumber: string;
  bNumber: string;
  hangupCause: string;
  sipTermStatus: number;
  billsec: number;
  duration: number;
  answered: boolean;
  cpdVerdict: string;
  cpdOperatorTag: string;
  cpdDetectMs: number;
}

export interface CallsPage {
  total: number;
  limit: number;
  offset: number;
  items: CallItem[];
}

export interface CallEventDetail {
  ts: string;
  eventType: string;
  fsNode: string;
  direction: string;
  hangupCause: string;
  sipTermStatus: number;
  billsec: number;
  duration: number;
  cpdVerdict: string;
  cpdSource: string;
  cpdOperatorTag: string;
  cpdDetectMs: number;
  bypassed: boolean;
}

export interface CallDetail {
  callUuid: string;
  channelUuid: string;
  operatorId: string;
  routeGroupId: number;
  aNumber: string;
  bNumber: string;
  events: CallEventDetail[];
}

/** Rango + filtros comunes (fechas YYYY-MM-DD). */
export interface RangeQuery {
  from: string;
  to: string;
  operatorId?: string;
  routeGroupId?: number;
}

export interface DashboardQuery extends RangeQuery {
  /** 1..50, default 10 */
  limit?: number;
}

export interface CallsQuery extends RangeQuery {
  cpdVerdict?: CpdVerdictFilter;
  hangupCause?: string;
  bypassed?: boolean;
  billsecMin?: number;
  billsecMax?: number;
  answered?: boolean;
  /** 1..500, default 50 */
  limit?: number;
  /** 0..100000 */
  offset?: number;
}

export type CallsExportQuery = Omit<CallsQuery, 'limit' | 'offset'> & {
  format: 'csv' | 'xlsx';
  filename?: string;
};

/** Para hourly/* el backend acepta ISO con hora (yyyy-mm-ddThh:mm:ss). */
export interface HourlyQuery {
  from: string;
  to: string;
  operatorId?: string;
  routeGroupId?: number;
}

const BASE = `${API_ENDPOINT.CORE}reports`;

/** Quita undefined/''/null para que el zod `.strict()` del backend no reciba llaves vacías. */
const clean = <T extends object>(q: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ) as Partial<T>;

export const ReportsService = {
  /** Histórico unificado: summary + top operators + top routes + hangup causes. */
  getDashboard: (params: DashboardQuery) =>
    HTTP.get<DashboardReport, DashboardReport>(`${BASE}/dashboard`, { params: clean(params) }),
  getSummary: (params: RangeQuery) =>
    HTTP.get<ReportSummary, ReportSummary>(`${BASE}/summary`, { params: clean(params) }),
  getDailyByOperator: (params: RangeQuery) =>
    HTTP.get<OperatorDailyRow[], OperatorDailyRow[]>(`${BASE}/operators`, {
      params: clean(params)
    }),
  getDailyByRoute: (params: RangeQuery) =>
    HTTP.get<RouteDailyRow[], RouteDailyRow[]>(`${BASE}/routes`, { params: clean(params) }),
  getHourlyByOperator: (params: HourlyQuery) =>
    HTTP.get<OperatorHourlyRow[], OperatorHourlyRow[]>(`${BASE}/hourly/operators`, {
      params: clean(params)
    }),
  getHourlyByRoute: (params: HourlyQuery) =>
    HTTP.get<RouteHourlyRow[], RouteHourlyRow[]>(`${BASE}/hourly/routes`, {
      params: clean(params)
    }),
  getHangupCauses: (params: DashboardQuery) =>
    HTTP.get<HangupCauseRow[], HangupCauseRow[]>(`${BASE}/hangup-causes`, {
      params: clean(params)
    }),
  getCalls: (params: CallsQuery) =>
    HTTP.get<CallsPage, CallsPage>(`${BASE}/calls`, { params: clean(params) }),
  getCallDetail: (callUuid: string) =>
    HTTP.get<CallDetail, CallDetail>(`${BASE}/calls/${encodeURIComponent(callUuid)}`),
  /** Export en streaming; devuelve el Blob para descargarlo en el navegador. */
  exportCalls: (params: CallsExportQuery) =>
    HTTP.get<Blob, Blob>(`${BASE}/calls/export`, { params: clean(params), responseType: 'blob' })
};

/** Dispara la descarga de un Blob en el navegador. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** URL del WebSocket del dashboard derivada de la API base (`https://host/v1/` → `wss://host/ws/dashboard`). */
export function getDashboardWsUrl(): string {
  const api = API_ENDPOINT.CORE.replace(/\/v1\/?$/, '');
  return api.replace(/^http/, 'ws') + '/ws/dashboard';
}
